import { useEffect, useRef, useState } from 'react';
import { Button, Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import type { CategoryRateRule, CategoryRateTier } from '@salaries/shared';
import { getClassCatalog, getRateCatalog } from '../../api/classCatalog';
import { FLAT_TIER, RateTiersEditor } from './RateTiersEditor';
import { DuplicatePanel } from './DuplicatePanel';

// Editor for the per-staff per-קטגוריה rates: one ₪-per-session rate for every
// class in a קטגוריה, optionally per branch and per participant bracket. Unlike
// the class bonus it REPLACES Arbox's per-meeting pay for matching classes.

type Row = {
  id: string;
  category_id: number | null;
  category: string;
  location: string | null;
  tiers: CategoryRateTier[];
};

let ridSeq = 0;
const newId = () => `c${ridSeq++}`;
const ALL_BRANCHES = '__all__';
// A קטגוריה is picked by Arbox's id; rules saved before the id existed by name.
const catKey = (c: { category_id: number | null; category: string }) =>
  c.category_id != null ? `#${c.category_id}` : c.category;

const toRows = (rules: CategoryRateRule[]): Row[] =>
  rules.map((r) => ({
    id: newId(),
    category_id: r.category_id ?? null,
    category: r.category,
    location: r.location,
    tiers: r.tiers,
  }));

// Half-filled rows (no קטגוריה yet) stay in the editor but are never saved.
const toRules = (rows: Row[]): CategoryRateRule[] =>
  rows
    .filter((r) => r.category && r.tiers.length)
    .map((r) => ({ category_id: r.category_id, category: r.category, location: r.location, tiers: r.tiers }));

// value/onChange are injected by the wrapping <Form.Item name="category_rates">.
export function CategoryRateFields({
  value = [],
  onChange,
}: {
  value?: CategoryRateRule[];
  onChange?: (v: CategoryRateRule[]) => void;
}) {
  const rateCatalogQ = useQuery({ queryKey: ['rate-catalog'], queryFn: getRateCatalog });
  const catalogQ = useQuery({ queryKey: ['class-catalog'], queryFn: getClassCatalog });

  // Local rows are the source of truth while editing (stable ids keep input
  // focus); re-seed only when `value` changes from outside (drawer re-open).
  const [rows, setRows] = useState<Row[]>(() => toRows(value));
  const lastSent = useRef<string>(JSON.stringify(value));
  useEffect(() => {
    const incoming = JSON.stringify(value);
    if (incoming !== lastSent.current) {
      lastSent.current = incoming;
      setRows(toRows(value));
    }
  }, [value]);

  const commit = (next: Row[]) => {
    setRows(next);
    const rules = toRules(next);
    lastSent.current = JSON.stringify(rules);
    onChange?.(rules);
  };
  const patch = (id: string, p: Partial<Row>) => commit(rows.map((r) => (r.id === id ? { ...r, ...p } : r)));
  // A saved rule's קטגוריה may no longer appear in recent meetings — keep it pickable.
  const categories = rateCatalogQ.data?.categories ?? [];
  const byKey = new Map<string, { category_id: number | null; category: string; class_names: string[] }>();
  for (const c of categories) byKey.set(catKey({ category_id: c.id, category: c.name }), { category_id: c.id, category: c.name, class_names: c.class_names });
  for (const r of rows)
    if (r.category && !byKey.has(catKey(r))) byKey.set(catKey(r), { category_id: r.category_id, category: r.category, class_names: [] });
  const classesOf = (r: Row) => byKey.get(catKey(r))?.class_names ?? [];
  const locOptions = [
    { value: ALL_BRANCHES, label: 'כל הסניפים' },
    ...(catalogQ.data?.locations ?? []).map((l) => ({ value: l, label: l })),
  ];
  const taken = (row: Row) =>
    rows.some((o) => o.id !== row.id && catKey(o) === catKey(row) && o.location === row.location);

  // שכפול: copy one row's tiers to every picked (קטגוריה × branch). A pair that
  // already has a row gets its tiers overwritten, so no pair ends up twice.
  const [dup, setDup] = useState<{ id: string; cats: string[]; locs: string[] } | null>(null);
  const openDup = (r: Row) => setDup({ id: r.id, cats: [catKey(r)], locs: [r.location ?? ALL_BRANCHES] });
  const planDup = () => {
    const src = rows.find((r) => r.id === dup?.id);
    if (!dup || !src) return { next: rows, added: 0, replaced: 0 };
    let next = rows;
    let added = 0;
    let replaced = 0;
    for (const k of dup.cats) {
      const c = byKey.get(k);
      if (!c) continue;
      for (const l of dup.locs) {
        const location = l === ALL_BRANCHES ? null : l;
        const same = (o: Row) => o.id !== src.id && !!o.category && catKey(o) === k && o.location === location;
        if (k === catKey(src) && location === src.location) continue;
        const tiers = src.tiers.map((t) => ({ ...t }));
        if (next.some(same)) {
          replaced++;
          next = next.map((o) => (same(o) ? { ...o, tiers } : o));
        } else {
          added++;
          next = [...next, { id: newId(), category_id: c.category_id, category: c.category, location, tiers }];
        }
      }
    }
    return { next, added, replaced };
  };
  const dupPlan = planDup();

  return (
    <div>
      <div className="text-small text-secondary-text mb-2">
        תעריף אחד לכל השיעורים בקטגוריה, במקום התעריף שמוגדר בארבוקס לכל סוג שיעור. שיעור מקטגוריה
        שהוגדר לה תעריף ישולם לפי התעריף כאן בלבד. שיעור מקטגוריה שלא הוגדר לה תעריף ימשיך להשתלם לפי
        ארבוקס. תעריף לסניף מסוים גובר על תעריף לכל הסניפים. אפשר לחלק למדרגות לפי מספר המשתתפים
        בשיעור (לפי שיטת החישוב של העסק). מספר משתתפים שלא נופל באף מדרגה ישולם לפי המדרגה הקרובה
        שמתחתיו.
      </div>

      {rows.map((r) => (
        <div key={r.id} className="border rounded px-3 py-3 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-small text-secondary-text">קטגוריה</span>
            <Select
              showSearch
              placeholder="קטגוריה"
              style={{ minWidth: 180 }}
              value={r.category ? catKey(r) : undefined}
              onChange={(k) => {
                const c = byKey.get(k);
                if (c) patch(r.id, { category_id: c.category_id, category: c.category });
              }}
              options={[...byKey.entries()].map(([k, c]) => ({ value: k, label: c.category }))}
              optionFilterProp="label"
              loading={rateCatalogQ.isLoading}
              notFoundContent={rateCatalogQ.isLoading ? 'טוען…' : 'לא נמצאו קטגוריות בשיעורים האחרונים'}
            />
            <span className="text-small text-secondary-text ms-2">סניף</span>
            <Select
              showSearch
              style={{ minWidth: 160 }}
              value={r.location ?? ALL_BRANCHES}
              onChange={(v) => patch(r.id, { location: v === ALL_BRANCHES ? null : v })}
              options={locOptions}
              optionFilterProp="label"
              loading={catalogQ.isLoading}
            />
            <span className="flex-1" />
            <Button size="small" type="text" disabled={!r.category} onClick={() => openDup(r)}>
              שכפול
            </Button>
            <Button size="small" danger type="text" onClick={() => commit(rows.filter((o) => o.id !== r.id))}>
              הסר
            </Button>
          </div>
          {r.category && classesOf(r).length > 0 && (
            <div className="text-small text-secondary-text mt-1">כולל: {classesOf(r).join(', ')}</div>
          )}
          {taken(r) && (
            <div className="text-small text-error-red mt-1">
              כבר קיים תעריף לקטגוריה הזו באותו סניף — רק אחד מהם יחול.
            </div>
          )}

          <RateTiersEditor tiers={r.tiers} onChange={(tiers) => patch(r.id, { tiers })} />
          {dup?.id === r.id && (
            <DuplicatePanel
              added={dupPlan.added}
              replaced={dupPlan.replaced}
              onApply={() => {
                commit(dupPlan.next);
                setDup(null);
              }}
              onCancel={() => setDup(null)}
            >
              <span className="text-small text-secondary-text">לקטגוריות</span>
              <Select
                mode="multiple"
                showSearch
                style={{ minWidth: 220, flex: 1 }}
                value={dup.cats}
                onChange={(cats) => setDup({ ...dup, cats })}
                options={[...byKey.entries()].map(([k, c]) => ({ value: k, label: c.category }))}
                optionFilterProp="label"
              />
              <span className="text-small text-secondary-text ms-2">בסניפים</span>
              <Select
                mode="multiple"
                showSearch
                style={{ minWidth: 200, flex: 1 }}
                value={dup.locs}
                onChange={(locs) => setDup({ ...dup, locs })}
                options={locOptions}
                optionFilterProp="label"
              />
            </DuplicatePanel>
          )}
        </div>
      ))}

      <Button onClick={() => commit([...rows, { id: newId(), category_id: null, category: '', location: null, tiers: [FLAT_TIER] }])}>
        + תעריף לקטגוריה
      </Button>
    </div>
  );
}
